import { Injectable, inject } from '@angular/core';

import { ApolloError } from '@apollo/client/errors';
import { BehaviorSubject, Observable, ReplaySubject, iif, map, merge, of, shareReplay, switchMap, tap } from 'rxjs';

import {
  Account,
  Contact,
  Customer,
  Employee,
  FleetMasterContract,
  FleetMasterContractStatus,
  Permission,
  UserContext
} from '@hdms/shared/common-types';
import { BaseTrackingDto, CookieService, EnvironmentService, removeEmptyObjectProps } from '@hdms/shared/util-core';

import {
  SHIPTO_ID_COOKIE_NAME,
  SSO_ORIGIN_CUSTOMER_ID_COOKIE_NAME,
  SSO_TS_CONTACT_ID_COOKIE_NAME,
  SSO_TS_CUSTOMER_ID_COOKIE_NAME,
  SSO_USER_ROLE_COOKIE_NAME
} from '../constants';
import { SsoUserRole } from '../types';

import { getAccount, getContact, getFleetMasterContact, isEmployee } from './helpers';

export interface UserContextInput {
  data?: UserContext;
  error?: ApolloError;
  accounts?: SelectableAccount[];
}

export interface SelectableAccount {
  account: Pick<Account, 'originId' | 'address'>;
  contact?: Contact;
}

/**
 * This service provides convenient access to UserContext data including values taken from cookies.
 * The service requires the actual GraphQL data to be applied from the outside (UserContextLoaderService),
 * in order to avoid circular dependencies e.g. when being injected into ApolloLink.
 */
@Injectable({
  providedIn: 'root'
})
export class UserContextService {
  private hasProps = false;
  private context: UserContext = {} as UserContext;
  private accounts: SelectableAccount[] = [];
  private updateError: ApolloError;
  private loginRequired = false;

  private readonly env = inject(EnvironmentService);
  private readonly cookie = inject(CookieService);
  private readonly baseTrackingDto = inject(BaseTrackingDto);

  private readonly updateSubject = new ReplaySubject<UserContext>(1);
  private readonly contextSubject = new BehaviorSubject<Observable<UserContext>>(this.updateSubject);
  private readonly context$ = this.contextSubject.pipe(switchMap(obs => obs));

  private ssoUserRoleFromCookie = this.getUserRoleFromCookie();
  private customerIdFromCookie = this.getCustomerIdFromCookie();
  private readonly preselectedShipTo = this.getPreselectedShipToId();

  constructor() {
    this.checkSsoCookieValidity();
  }

  /**
   * Returns true if context has data.
   * Usually this will be the case after data has been loaded successfully, but
   * it still can return false in case of a request error or if no data received.
   */
  get hasData(): boolean {
    return this.hasProps;
  }

  get contact(): Contact {
    return getContact(this.context);
  }

  get email(): string {
    return this.context.email;
  }

  get account(): Account {
    return getAccount(this.context);
  }

  get userAccounts(): SelectableAccount[] {
    // when loggedIn as AM, it mustn't be possible to switch accounts
    return !this.isAccountManager ? this.accounts : [];
  }

  /**
   * Negating alias of `isLoggedIn`
   */
  get isAnonymous(): boolean {
    return !this.isLoggedIn;
  }

  /**
   * Logged-in user will be any user which can log in
   * on the website, even users with pending registration.
   * It will be evaluated based on sso cookie presence.
   */
  get isLoggedIn(): boolean {
    return !!this.ssoUserRole;
  }

  /**
   * After account has been created, it still can take until
   * it's being synced in the BE. As long this flag will return true.
   * TODO: band-aid for the time being unless CCMSv2 migration is done.
   *  to be cleaned up afterwards including all related logic/ui
   */
  get isAccountSync(): boolean {
    return !!this.env.legacyEnv?.isAccountSync;
  }

  /**
   * Registered users will be all logged-in users, except ones
   * with pending registration. For verifying access to pages
   * e.g. in myaccount section, using `isRegistered` is more
   * reliable than just checking `isLoggedIn`.
   */
  get isRegistered(): boolean {
    return this.isLoggedIn && !this.isPendingRegistration;
  }

  /**
   * As long a user didn't complete registration, he/she will be
   * handled as a logged-in user, but not as a registered one.
   * User role cookie will be set to  `USER`.
   */
  get isPendingRegistration(): boolean {
    return this.ssoUserRole === SsoUserRole.USER;
  }

  /**
   * Returns if a user is Hilti employee
   */
  get isEmployee(): boolean {
    return isEmployee(this.context);
  }

  /**
   * Returns if a user is AM / TS, acting on behalf of a customer
   */
  get isAccountManager(): boolean {
    return this.ssoUserRole === SsoUserRole.ACCOUNT_MANAGER;
  }

  /**
   * Returns if a user is eVoucher backoffice user
   */
  get isEVoucherBackofficeUser(): boolean {
    return this.ssoUserRole === SsoUserRole.EVOUCHER_BACKOFFICE_USER;
  }

  /**
   * Returns if a user is customer
   */
  get isCustomer(): boolean {
    return this.ssoUserRole === SsoUserRole.CUSTOMER;
  }

  /**
   * Returns if user has fleet contract.
   * Falls back to legacy env prop if data not loaded
   */
  get hasFleetContract(): boolean {
    return Boolean(
      this.hasProps
        ? this.account.isFleet || this.fleetMasterContract?.status === FleetMasterContractStatus.ACTIVE
        : this.env.legacyEnv?.fleetContract
    );
  }

  /**
   * Returns if user requires to log in, e.g. for accessing current route
   */
  get requiresLogin(): boolean {
    return this.loginRequired;
  }

  /**
   * Will be set e.g. by a route-guard for indicating that a login is required
   * @param status
   */
  set requiresLogin(status: boolean) {
    if (!this.ssoUserRole) {
      this.loginRequired = status;
    }
  }

  /**
   * Returns Customer OriginId
   * Either taken from context data (if loaded)
   * otherwise from potentially existing cookies
   */
  get customerOriginId(): string {
    return this.hasProps ? this.account.originId : this.customerIdFromCookie;
  }

  get ssoUserRole(): SsoUserRole {
    return this.ssoUserRoleFromCookie;
  }

  /**
   * Returns Contact OriginId
   * Either taken from context data (if loaded)
   * otherwise from potentially existing cookies
   */
  get contactOriginId(): string {
    return this.hasProps ? this.contact.originId : this.cookie.get<string>(SSO_TS_CONTACT_ID_COOKIE_NAME);
  }

  /**
   * Returns permissions of current user
   * As long GAM isn't re-implemented, Permissions will only be returned for employee user type
   * TODO: delete during cleanup of previous implementation of auth management
   */
  get employeePermissions(): Permission[] {
    return (this.context as Employee).employeeRole?.permissions || ([] as Permission[]);
  }

  /**
   * Returns global authorization permissions of current user
   */
  get permissions(): string[] {
    return (this.context as Customer).permissions || ([] as string[]);
  }

  /**
   * Returns if customer has Authorization Management Enabled
   */
  get isAuthorizationManagementEnabled(): boolean {
    // TODO: AuthMgmt - Removed 'Boolean((this.context as Customer)?.isAuthorizationManagementEnabled)'
    // /libs/myaccount-authmanagement/feature/README.md
    return false;
  }

  /**
   * Preselected ShipTo ID would only be populated if logged-in as AM
   */
  get preselectedShipToId(): string {
    return this.preselectedShipTo;
  }

  /**
   * Returns if it's unsafe to render stateful content containing user data.
   * Will return true on server side when page is being requested by logged-in user.
   * On client side it will always return false.
   */
  get preventUserDataCaching(): boolean {
    return this.env.ssrCachingEnabled && !!this.ssoUserRole;
  }

  /**
   * Return potential error happened when retrieving data
   */
  get error(): ApolloError {
    return this.updateError;
  }

  private get fleetMasterContract(): FleetMasterContract {
    return getFleetMasterContact(this.context);
  }

  /**
   * Returns if context should be loaded from GraphQL:
   * - in standalone mode when ssoUserRole cookie is present
   * - in existing Hybris fullMode scenario only when user is logged in
   * - in mixed mode and all other cases return false
   * @private
   */
  private get shouldLoadContext(): boolean {
    const { isMixedMode, hostContext, isStandalone, legacyEnv: { loggedIn } = {}, ssrCachingEnabled } = this.env;
    return (!isStandalone && (!isMixedMode || hostContext) && loggedIn) || (!!this.ssoUserRole && !ssrCachingEnabled);
  }

  /**
   * Will be called from UserContextLoaderService for setting
   * context$ observable, merged with internal updateSubject.
   * Depending on env conditions, `this.context$` will request
   * from GraphQL or just return (unset) context data.
   * Still it will emit data applied via `setData()`.
   * In MixedMode scenario the observable mustn't be changed,
   * in order to not overwrite initial data derived from legacy env.
   * @param context$
   */
  setContext$(context$: Observable<UserContextInput>): void {
    this.contextSubject.next(
      merge(
        iif(() => this.shouldLoadContext, context$, of({ data: this.getData() })).pipe(
          tap(data => this.updateData(data)),
          map(_ => this.getData())
        ),
        this.updateSubject
      ).pipe(shareReplay(1))
    );
  }

  /**
   * Set context data directly from outside.
   * Be careful with manually invoking this method,
   * as then existing data could become overwritten.
   * @param input
   * @param reset
   */
  setData(input: UserContextInput, reset = false): void {
    this.updateData(input, reset);

    this.updateSubject.next(this.getData());
  }

  getData(): UserContext {
    return { ...this.context };
  }

  update$<T extends UserContext>(): Observable<T> {
    return this.context$ as Observable<T>;
  }

  updateOriginCustomerId(id: string): void {
    const options = { 'Max-Age': 1704085200, path: '/' };
    this.cookie.set(SSO_ORIGIN_CUSTOMER_ID_COOKIE_NAME, id, options);
    this.cookie.set(SSO_USER_ROLE_COOKIE_NAME, SsoUserRole.CUSTOMER, options);

    this.ssoUserRoleFromCookie = this.getUserRoleFromCookie();
    this.customerIdFromCookie = this.getCustomerIdFromCookie();
  }

  forceErrorState(): void {
    this.updateError = {} as ApolloError;
  }

  /**
   * Updates internal context with applied data
   * and sets the service status.
   * Additionally, it triggers base tracking data updates
   * @param input
   * @param reset
   * @private
   */
  private updateData(input: UserContextInput, reset = false): void {
    const { data, error, accounts } = input || {};
    this.context = { ...(reset || !data ? null : this.context), ...data };
    this.updateError ||= error;
    this.hasProps = !!Object.keys(this.context).length;
    this.accounts = accounts || [];

    this.setBaseTrackingData();
  }

  /**
   * If logged-in as AM, return preselected ShipTo ID taken from cookie
   * @private
   */
  private getPreselectedShipToId(): string {
    return this.isAccountManager ? this.cookie.get(SHIPTO_ID_COOKIE_NAME) : null;
  }

  /**
   * Return customerOriginId taken from a cookie,
   * depending on if account manager cookie has been set or not.
   * @private
   */
  private getCustomerIdFromCookie(): string {
    const customerIdCookieName = this.isAccountManager
      ? SSO_TS_CUSTOMER_ID_COOKIE_NAME
      : SSO_ORIGIN_CUSTOMER_ID_COOKIE_NAME;
    return this.cookie.get<string>(customerIdCookieName);
  }

  private getUserRoleFromCookie(): SsoUserRole {
    const cookieVal = this.cookie.get<SsoUserRole>(SSO_USER_ROLE_COOKIE_NAME);
    return Object.values(SsoUserRole).includes(cookieVal) ? cookieVal : null;
  }

  /**
   * In case of improperly set sso cookies, reset ssoUserRole for handling session as anonymous.
   * @private
   */
  private checkSsoCookieValidity(): void {
    if ((this.isCustomer || this.isAccountManager) && !this.customerIdFromCookie) {
      this.ssoUserRoleFromCookie = null;
    }
  }

  /**
   * Update base tracking data via DTO to be passed on to AnalyticsService
   * @private
   */
  private setBaseTrackingData(): void {
    this.baseTrackingDto.updateData(
      removeEmptyObjectProps({
        loggedIn: this.isLoggedIn,
        account: this.customerOriginId,
        contact_id: this.contactOriginId,
        fleetContract: this.hasFleetContract,
        hiltiUser: this.isEmployee,
        hiltiSession: this.isAccountManager,
        jobFunction: this.contact.function?.id,
        // TODO: populate all still missing properties like the following
        cloudID: null,
        recProdGroup: null,
        potentialClass: null,
        trade: null
      })
    );
  }
}
